import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { useAuth } from '../../../context/AuthContext';
import { getContentById, createContent, updateContent } from '../../../services/contentService';
import { uploadImage } from '../../../services/uploadService';
import { searchProducts } from '../../../services/productService';

const ContentFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const isEdit = Boolean(id && id !== 'new');
    const isView = window.location.pathname.includes('/view');

    // Debug authentication
    console.log('ContentFormPage - User:', user);
    console.log('ContentFormPage - Token available:', !!token);

    const [formData, setFormData] = useState({
        slug: '',
        title: '',
        eyebrow: '',
        description: '',
        content: '',
        image: '',
        contentType: 'blog',
        status: 'published',
        metaTitle: '',
        metaDescription: '',
        tags: '',
        readTime: '',
        featuredProducts: []
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    
    // Featured products states
    const [productSearch, setProductSearch] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);

    useEffect(() => {
        if (isEdit || isView) {
            fetchContent();
        }
    }, [id]);

    const fetchContent = async () => {
        try {
            setLoading(true);
            const response = await getContentById(id, token);
            const content = response.data;
                setFormData({
                    slug: content.slug || '',
                    title: content.title || '',
                    eyebrow: content.eyebrow || '',
                    description: content.description || '',
                    content: content.content || '',
                    image: content.image || '',
                    contentType: content.contentType || 'blog',
                    status: content.status || 'published',
                    metaTitle: content.metaTitle || '',
                    metaDescription: content.metaDescription || '',
                    tags: content.tags ? content.tags.join(', ') : '',
                    readTime: content.readTime || '',
                    featuredProducts: content.featuredProducts || []
                });
                
                // Set image preview if image exists
                if (content.image) {
                    // If the image is a relative URL, prepend the backend URL
                    const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
                    const imageUrl = content.image.startsWith('http') 
                        ? content.image 
                        : `${backendUrl}${content.image}`;
                    setImagePreview(imageUrl);
                }
        } catch (error) {
            console.error('Error fetching content:', error);
            toast.error('Error fetching content');
            navigate('/admin/content');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Auto-generate slug from title
        if (name === 'title' && !isEdit) {
            const slug = value
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/--+/g, '-')
                .trim();
            setFormData(prev => ({
                ...prev,
                slug: slug
            }));
        }
    };
    
    // Handle product search
    const handleProductSearch = async (searchTerm) => {
        setProductSearch(searchTerm);
        
        if (!searchTerm || searchTerm.trim().length < 2) {
            setProductSearchResults([]);
            return;
        }
        
        try {
            setSearchingProducts(true);
            const response = await searchProducts(searchTerm, 10);
            setProductSearchResults(response.data || []);
        } catch (error) {
            console.error('Error searching products:', error);
            setProductSearchResults([]);
        } finally {
            setSearchingProducts(false);
        }
    };
    
    // Add product to featured products
    const handleAddProduct = (product) => {
        // Check if product is already added
        const isAlreadyAdded = formData.featuredProducts.some(p => 
            (typeof p === 'object' ? p._id : p) === product._id
        );
        
        if (isAlreadyAdded) {
            toast.info('Product already added');
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            featuredProducts: [...prev.featuredProducts, product]
        }));
        
        // Clear search
        setProductSearch('');
        setProductSearchResults([]);
        toast.success('Product added to featured picks');
    };
    
    // Remove product from featured products
    const handleRemoveProduct = (productId) => {
        setFormData(prev => ({
            ...prev,
            featuredProducts: prev.featuredProducts.filter(p => 
                (typeof p === 'object' ? p._id : p) !== productId
            )
        }));
        toast.success('Product removed from featured picks');
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size must be less than 5MB');
                return;
            }

            setImageFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageUpload = async () => {
        if (!imageFile) {
            toast.error('Please select an image first');
            return;
        }

        try {
            setUploadingImage(true);
            console.log('Uploading image:', imageFile.name);
            console.log('Token available:', !!token);
            console.log('Token value:', token ? token.substring(0, 20) + '...' : 'No token');
            
            const response = await uploadImage(imageFile, token);
            console.log('Upload response:', response);
            
            setFormData(prev => ({
                ...prev,
                image: response.data.imageUrl
            }));
            
            console.log('Updated form data with image URL:', response.data.imageUrl);
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Error uploading image:', error);
            console.error('Error details:', error.response?.data);
            console.error('Error status:', error.response?.status);
            toast.error(`Failed to upload image: ${error.response?.data?.message || error.message}`);
        } finally {
            setUploadingImage(false);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setFormData(prev => ({
            ...prev,
            image: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const submitData = {
                ...formData,
                tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
                // Convert featured products to array of IDs
                featuredProducts: formData.featuredProducts.map(p => typeof p === 'object' ? p._id : p)
            };

            console.log('Submitting content with data:', submitData);
            console.log('Image URL being submitted:', submitData.image);
            console.log('Featured Products IDs:', submitData.featuredProducts);

            if (isEdit) {
                const response = await updateContent(id, submitData, token);
                console.log('Update response:', response);
                toast.success('Content updated successfully');
            } else {
                const response = await createContent(submitData, token);
                console.log('Create response:', response);
                toast.success('Content created successfully');
            }
            navigate('/admin/content');
        } catch (error) {
            console.error('Error saving content:', error);
            toast.error('Error saving content');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="max-w-4xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isView ? 'View Blog Post' : isEdit ? 'Edit Blog Post' : 'Create New Blog Post'}
                    </h1>
                    <p className="text-gray-600">
                        {isView ? 'View blog post details' : isEdit ? 'Update blog post information' : 'Create a new blog post for "Blog" page'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="bg-[#f8f8f8] shadow-sm rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    disabled={isView}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="Enter page title"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Slug
                                </label>
                                <input
                                    type="text"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    disabled={isView}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="Leave empty to auto-generate from title"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    If left empty, will be auto-generated from the title
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Eyebrow Text
                                </label>
                                <input
                                    type="text"
                                    name="eyebrow"
                                    value={formData.eyebrow}
                                    onChange={handleChange}
                                    disabled={isView}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="e.g., Legal, About"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Content Type *
                                </label>
                                <input
                                    type="text"
                                    name="contentType"
                                    value="Blog Post"
                                    disabled={true}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                />
                                <input type="hidden" name="contentType" value="blog" />
                                <p className="mt-1 text-xs text-gray-500">
                                    All content created here will be blog posts
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status *
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    disabled={isView}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                >
                                    <option value="published">Published</option>
                                    <option value="draft">Draft</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Read Time (for blog posts)
                                </label>
                                <input
                                    type="text"
                                    name="readTime"
                                    value={formData.readTime}
                                    onChange={handleChange}
                                    disabled={isView}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="e.g., 5 min read"
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                disabled={isView}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                placeholder="Brief description for the page header"
                            />
                        </div>

                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Featured Image
                            </label>
                            
                            <div className="space-y-4">
                                <div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        disabled={isView || uploadingImage}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Select an image file (JPG, PNG, GIF, etc.) - Max 5MB
                                    </p>
                                </div>

                                {imageFile && !formData.image && (
                                    <div className="flex items-center space-x-4">
                                        <button
                                            type="button"
                                            onClick={handleImageUpload}
                                            disabled={uploadingImage || isView}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            disabled={isView}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}

                                {imagePreview && (
                                    <div className="mt-4">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="w-48 h-32 object-cover rounded-lg border"
                                        />
                                        {formData.image && (
                                            <div className="mt-2">
                                                <button
                                                    type="button"
                                                    onClick={removeImage}
                                                    disabled={isView}
                                                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                                                >
                                                    Remove Image
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-800">
                                    <strong>✨ Blog Template Features:</strong> This image will be used as the blog post cover image, in blog listings, homepage "Stay in the Know" section, and product recommendations. Recommended size: 1200x600px
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#f8f8f8] shadow-sm rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Content</h2>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                HTML Content *
                            </label>
                            <textarea
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                disabled={isView}
                                required
                                rows={15}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 font-mono text-sm"
                                placeholder="Enter HTML content here..."
                            />
                            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-800">
                                    <strong>🎨 Blog Template Styling:</strong> Your content will automatically get professional styling with table of contents, affiliate disclosure, and product recommendations based on keywords.
                                </p>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                                You can use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a&gt;, etc. H2 and H3 tags will automatically appear in the table of contents.
                            </p>
                        </div>
                    </div>

                    <div className="bg-[#f8f8f8] shadow-sm rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">SEO & Meta</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Meta Title
                                </label>
                                <input
                                    type="text"
                                    name="metaTitle"
                                    value={formData.metaTitle}
                                    onChange={handleChange}
                                    disabled={isView}
                                    maxLength={60}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="SEO title (60 characters max)"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Meta Description
                                </label>
                                <textarea
                                    name="metaDescription"
                                    value={formData.metaDescription}
                                    onChange={handleChange}
                                    disabled={isView}
                                    maxLength={160}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="SEO description (160 characters max)"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tags (comma-separated)
                                </label>
                                <input
                                    type="text"
                                    name="tags"
                                    value={formData.tags}
                                    onChange={handleChange}
                                    disabled={isView}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="tag1, tag2, tag3"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#f8f8f8] shadow-sm rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Featured Products</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Search Products
                                </label>
                                <input
                                    type="text"
                                    value={productSearch}
                                    onChange={(e) => handleProductSearch(e.target.value)}
                                    disabled={isView}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                    placeholder="Search by product name or brand..."
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Select 2-4 products to feature in this blog post
                                </p>
                                
                                {/* Search Results */}
                                {productSearchResults.length > 0 && !isView && (
                                    <div className="mt-2 border border-gray-200 rounded-lg max-h-64 overflow-y-auto bg-white">
                                        {productSearchResults.map(product => (
                                            <div
                                                key={product._id}
                                                className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                                                onClick={() => handleAddProduct(product)}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    {product.imageUrl && (
                                                        <img 
                                                            src={product.imageUrl} 
                                                            alt={product.name}
                                                            className="w-12 h-12 object-cover rounded"
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="font-medium text-sm">
                                                            {product.brand} - {product.name}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            ${product.price} • Rating: {product.rating}/5
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {searchingProducts && (
                                    <div className="mt-2 text-sm text-gray-500 text-center py-2">
                                        Searching...
                                    </div>
                                )}
                            </div>
                            
                            {/* Selected Featured Products */}
                            {formData.featuredProducts.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Selected Products ({formData.featuredProducts.length})
                                    </label>
                                    <div className="space-y-2">
                                        {formData.featuredProducts.map((product, index) => {
                                            const prod = typeof product === 'object' ? product : { _id: product };
                                            return (
                                                <div
                                                    key={prod._id || index}
                                                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                                                >
                                                    <div className="flex items-center space-x-3">
                                                        {prod.imageUrl && (
                                                            <img 
                                                                src={prod.imageUrl} 
                                                                alt={prod.name}
                                                                className="w-12 h-12 object-cover rounded"
                                                            />
                                                        )}
                                                        <div>
                                                            <div className="font-medium text-sm">
                                                                {prod.brand ? `${prod.brand} - ${prod.name}` : `Product ID: ${prod._id}`}
                                                            </div>
                                                            {prod.price && (
                                                                <div className="text-xs text-gray-500">
                                                                    ${prod.price} • Rating: {prod.rating}/5
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {!isView && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveProduct(prod._id)}
                                                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>💡 Pro Tip:</strong> If you don't select products manually, the system will automatically recommend products based on the blog post's keywords and category.
                                </p>
                            </div>
                        </div>
                    </div>

                    {!isView && (
                        <div className="flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/content')}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : isEdit ? 'Update Content' : 'Create Content'}
                            </button>
                        </div>
                    )}

                    {isView && (
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/content')}
                                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                            >
                                Back to Content
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </AdminLayout>
    );
};

export default ContentFormPage;
